import {Query, Predicate, BrickContext} from 'olympe';
import React, {useEffect, useState} from 'react';
import {Table, TableContainer, TableCell, TableRow, TableHead, TableBody, TextField, Autocomplete} from '@mui/material';
import PropTypes from 'prop-types';
import DeleteIconWithPopup from '../utils/DeleteIconWithPopup.jsx';
import {EZPRSelect} from '../utils/EZPRSelect.jsx';
import EditIcon from '@mui/icons-material/Edit';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {
    defaultCompetitiveBiddingTopicOptions,
    exceptionalCompetitiveBiddingTopicOptions,
    defaultCollectiveMedicalVendorTopicOptions,
} from '../../_helpers';
import {FILE_TAG} from '../../../utils/constants';

/**
 * @param {AttachmentsTable.propTypes} props
 * @return {JSX.Element}
 */
export function AttachmentsTable(props) {
    const useRequestStore = requestStores.get(props.prTag);

    const mainAttachments = useRequestStore((state) => state.mainAttachments);
    const setMainAttachmentTopic = useRequestStore((state) => state.setMainAttachmentTopic);
    const setCBAttachmentTopic = useRequestStore((state) => state.setCBAttachmentTopic);
    const isTotalGreaterThanThreshold = useRequestStore((state) => state.isTotalGreaterThanThreshold);
    const competitiveBiddingJustificationAttachments = useRequestStore((state) => state.competitiveBiddingJustificationAttachments);
    const deleteMainAttachment = useRequestStore((state) => state.deleteMainAttachment);
    const deleteCBAttachment = useRequestStore((state) => state.deleteCBAttachment);
    const deleteAllCBAttachment = useRequestStore((state) => state.deleteAllCBAttachment);
    const hasException = useRequestStore((state) => state.hasException);
    const supplierType = useRequestStore((state)=> state.supplierType);
    const currentAttachments = props.attachmentsType === 'main' ? mainAttachments : competitiveBiddingJustificationAttachments;
    const currentAttachmentsDeleteFunction = props.attachmentsType === 'main' ? deleteMainAttachment : deleteCBAttachment;

    let competitiveBiddingTopicOptions = defaultCompetitiveBiddingTopicOptions;

    if (hasException) {
        competitiveBiddingTopicOptions = defaultCompetitiveBiddingTopicOptions.concat(...exceptionalCompetitiveBiddingTopicOptions);
    } else {
        competitiveBiddingTopicOptions = defaultCompetitiveBiddingTopicOptions;
    }
    const [isEditingTopic, setIsEditingTopic] = useState(-1);
    // This useEffect checks and deletes all the attachments in the CB Justification Section, if the value is below CB Justification Threshold.
    useEffect(() => {
        if (competitiveBiddingJustificationAttachments && !isTotalGreaterThanThreshold) {
            deleteAllCBAttachment(-1);
        }
    }, [isTotalGreaterThanThreshold]);

    const startEditTopic = (idx) => {
        setIsEditingTopic(idx);
    };

    const finishEditTopic = (newTopic, attachmentTag) => {
        setMainAttachmentTopic(newTopic, attachmentTag);
        if (defaultCollectiveMedicalVendorTopicOptions.includes(newTopic)) {
            props.onChange && props.onChange(mainAttachments);
        }
        setIsEditingTopic(-1);
    };

    const handleClickOnDelete = (att, idx) => {
        // TODO Here we should delete the file from the database, but only on save or submit.
        currentAttachmentsDeleteFunction(idx);
    };

    const handleTopicChange = (newTopic, attachmentTag) => {
        setMainAttachmentTopic(newTopic, attachmentTag);
        props.onChange && props.onChange(mainAttachments);
    };

    const allowedOneTimeTopics = props.from === 'cmvPopup' ?
        defaultCollectiveMedicalVendorTopicOptions :
        props.from === 'otvPopup' ?
            defaultCollectiveMedicalVendorTopicOptions.slice(0, 1) :
            null;

    let filteredAttachments = currentAttachments;

    if (props.attachmentsType === 'main' && allowedOneTimeTopics) {
        filteredAttachments = currentAttachments.filter((att) => {
            const topic = att.topic || att._topic;
            return !topic || allowedOneTimeTopics.includes(topic);
        });
    }

    return (
        <TableContainer sx={{borderRadius: '4px', marginBottom: '2rem', tableLayout: 'fixed'}}>
            <Table sx={{tableLayout: 'fixed'}} size="small">
                <TableHead key={'header'} className={'pr-editor-attachments-table-header'}>
                    <TableRow className={'pr-table-rowheader'}>
                        <TableCell className={'pr-editor-attachments-table-header-content'}>Topic</TableCell>
                        <TableCell className={'pr-editor-attachments-table-header-content'}>User</TableCell>
                        <TableCell className={'pr-editor-attachments-table-header-content'}>Date</TableCell>
                        <TableCell className={'pr-editor-attachments-table-header-content'}>Name</TableCell>
                        <TableCell className={'pr-editor-attachments-table-header-content'} style={{width: '40px', paddingLeft: 0, paddingRight: 0}}/>
                    </TableRow>
                </TableHead>
                <TableBody sx={{backgroundColor: 'rgb(248,248,248)'}}>
                    {filteredAttachments.map((att, idx) => (
                        <TableRow key={att.file}>
                            <TableCell className={'pr-editor-attachments-table-cell'}>
                                {props.attachmentsType === 'main' && props.from === 'expandedPr' ?
                                    <Autocomplete
                                        clearOnBlur={false}
                                        freeSolo
                                        options={supplierType === 'CMV' ? defaultCollectiveMedicalVendorTopicOptions : supplierType === 'OTV' ? defaultCollectiveMedicalVendorTopicOptions.slice(0, 1) : []}
                                        size={'small'}
                                        value={att.topic}
                                        onBlur={(event) => {
                                            if (event.target.tagName === 'INPUT') {
                                                finishEditTopic(event.target.value, att.file);
                                            }
                                        }}
                                        disabled={props?.disabled || isEditingTopic !== idx}
                                        open={isEditingTopic === idx }
                                        onChange={(_, value)=> {
                                            if ( value && value !== '' ) {
                                                finishEditTopic(value, att.file);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                id={'topicTextField' + idx}
                                                fullWidth={true}
                                                variant={'standard'}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    style: {
                                                        color: 'inherit',
                                                        WebkitTextFillColor: 'inherit',
                                                    },
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => startEditTopic(idx)}
                                                                edge="end"
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                style={{
                                                    color: 'inherit',
                                                }}
                                            />
                                        )}
                                    /> :
                                    props.attachmentsType === 'main' && (props.from === 'otvPopup' || props.from === 'cmvPopup') ?
                                        <Autocomplete
                                            forcePopupIcon={true}
                                            clearOnBlur={false}
                                            freeSolo
                                            options={(props.from === 'cmvPopup' || supplierType === 'CMV') ? defaultCollectiveMedicalVendorTopicOptions : defaultCollectiveMedicalVendorTopicOptions.slice(0, 1)}
                                            size={'small'}
                                            value={att.topic}
                                            onBlur={(event) => handleTopicChange(event.target.value, att.file)}
                                            disabled={props?.disabled}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant={'outlined'}
                                                    rows={1}
                                                />
                                            )}
                                        />:
                                        <EZPRSelect
                                            style={{width: '80%'}}
                                            options={competitiveBiddingTopicOptions}
                                            value={att.topic}
                                            onChange={(newValue) => setCBAttachmentTopic(newValue, att.file)}
                                            minWidth={'none'}
                                            placeholder={'Please choose the topic'}
                                            readOnly={props?.disabled}
                                            preventEdit={true}
                                        />
                                }
                            </TableCell>
                            <TableCell className={'pr-editor-attachments-table-cell'} data-cy={'attachment-user'}>
                                {att.user}
                            </TableCell>
                            <TableCell className={'pr-editor-attachments-table-cell'}>{new Date(att.date).toLocaleString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}</TableCell>
                            <TableCell className={'pr-editor-attachments-table-cell'} sx={{textDecoration: 'underline'}}>
                                <div style={{cursor: 'pointer'}} onClick={() => {
                                    Query
                                        .instancesOf(FILE_TAG)
                                        .filter(Predicate.in(att.file))
                                        .execute()
                                        .then((files) => {
                                            const f = files.toArray();
                                            f[0].saveAs(att.name);
                                        });
                                }}>
                                    {att.name}
                                </div>
                            </TableCell>
                            <TableCell className={'pr-editor-attachments-table-cell'} style={{width: '40px', paddingLeft: 0, paddingRight: 0}}>
                                <DeleteIconWithPopup
                                    onConfirm={() => handleClickOnDelete(att, idx)}
                                    buttonProps={{disabled: props?.disabled}}
                                />
                            </TableCell>
                        </TableRow>
                    ),
                    )
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
}

AttachmentsTable.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    attachmentsType: PropTypes.oneOf(['main', 'cb']),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    from: PropTypes.string,
};
